import {
  Children,
  ReactNode,
  createContext,
  useState,
  useContext,
  useEffect,
} from 'react';

import {
  LayoutMultiViewStyled,
  LayoutMultiViewTrackStyled,
  LayoutMultiViewPageStyled,
} from './layout.styles';
import { useKeyBoardTrap } from 'approot/shared/ally/keyboard.hooks';

type LayoutMultiViewContextProps = {
  goForward: () => void;
  goBack: () => void;
  goTo: (index: number) => void;
};
export const LayoutMultiViewContext =
  createContext<LayoutMultiViewContextProps>({
    goForward: () => {},
    goBack: () => {},
    goTo: () => {},
  });

type LayoutMultiViewPageContextProps = {
  isActive: boolean;
  isBehind: boolean;
  previousPageId?: string;
};
export const LayoutMultiViewPageContext =
  createContext<LayoutMultiViewPageContextProps>({
    isActive: false,
    isBehind: false,
    previousPageId: undefined,
  });

type Props = {
  children: ReactNode;
  onViewChange?: (index: number) => void;
};

export const LayoutMultiView = ({ children, onViewChange }: Props) => {
  const [index, setIndex] = useState<number>(0);
  const [moving, setMoving] = useState<boolean>(false);

  useEffect(() => {
    if (onViewChange) onViewChange(index);
  }, [index, onViewChange]);

  return (
    <LayoutMultiViewContext.Provider
      value={{
        goForward: () => {
          if (moving) return;
          setIndex(index + 1);
          setMoving(true);
          setTimeout(() => {
            setMoving(false);
          }, 400);
        },
        goBack: () => {
          if (moving) return;
          setIndex(index - 1);
          setMoving(true);
          setTimeout(() => {
            setMoving(false);
          }, 400);
        },
        goTo: index => {
          if (moving) return;
          setIndex(index);
          setMoving(true);
          setTimeout(() => {
            setMoving(false);
          }, 400);
        },
      }}
    >
      <LayoutMultiViewStyled data-selector="multi-view-drawer">
        <LayoutMultiViewTrackStyled>
          {Children.toArray(children).map((child, idx) => {
            return (
              <LayoutMultiViewPageContext.Provider
                key={idx}
                value={{
                  isActive: index === idx,
                  isBehind: index > idx,
                  previousPageId: `${idx - 1}`,
                }}
              >
                {child}
              </LayoutMultiViewPageContext.Provider>
            );
          })}
        </LayoutMultiViewTrackStyled>
      </LayoutMultiViewStyled>
    </LayoutMultiViewContext.Provider>
  );
};

type LayoutMultiViewProps = {
  children: ReactNode;
  onClose?: () => void;
};
export const LayoutMultiViewPage = ({
  children,
  onClose,
}: LayoutMultiViewProps) => {
  const { goBack } = useContext(LayoutMultiViewContext);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const { isActive, isBehind, previousPageId } = useContext(
    LayoutMultiViewPageContext
  );

  useKeyBoardTrap({
    containerRef,
    isActive: !!isActive,
    captureKeypress: !!isActive,
    close: () => {
      if (onClose) {
        onClose();
      } else if (previousPageId) {
        goBack();
      }
    },
  });

  return (
    <LayoutMultiViewPageStyled
      isActive={isActive}
      isBehind={isBehind}
      ref={setContainerRef}
      tabIndex={-1}
    >
      <div>{children}</div>
    </LayoutMultiViewPageStyled>
  );
};

export const useMultiViewContext = () => useContext(LayoutMultiViewContext);

export const MultiView = ({
  children,
  defaultIndex,
}: {
  children: (provided: {
    activeIndex: number;
    goForward: () => void;
    goBack: () => void;
    goTo: (index: number) => void;
  }) => ReactNode;
  defaultIndex?: number;
}) => {
  const [active, setActive] = useState<number>(defaultIndex || 0);

  return (
    <LayoutMultiViewStyled data-selector="multi-view-drawer">
      <LayoutMultiViewTrackStyled>
        {children({
          activeIndex: active,
          goForward: () => setActive(active + 1),
          goBack: () => setActive(active - 1),
          goTo: (index: number) => setActive(index),
        })}
      </LayoutMultiViewTrackStyled>
    </LayoutMultiViewStyled>
  );
};

export type MultiViewProps = {
  activeIndex: number;
  goForward: () => void;
  goBack: () => void;
  goTo: (id: number) => void;
};

export const MultiViewPage = ({
  index,
  activeIndex,
  goBack,
  onClose,
  renderItem,
}: MultiViewProps & {
  index: number;
  onClose?: () => void;
  renderItem: (activeIndex: number) => ReactNode;
}) => {
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  const isActive = index === activeIndex;
  const isBehind = activeIndex > index;

  useKeyBoardTrap({
    containerRef,
    isActive,
    captureKeypress: isActive,
    close: () => {
      if (onClose) {
        onClose();
      } else {
        goBack();
      }
    },
  });

  return (
    <LayoutMultiViewPageStyled
      isBehind={isBehind}
      isActive={isActive}
      ref={setContainerRef}
      tabIndex={-1}
    >
      {!isBehind && !isActive ? null : renderItem(activeIndex)}
    </LayoutMultiViewPageStyled>
  );
};
